import {i18n} from '@online-offer/i18n';
import {Text} from 'platform/foundation';
import styled from 'styled-components';

import {useLocation} from 'react-router-dom';

import {isNil} from 'ramda';

import {useNavigate} from 'shared/web';

import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {OfferDirection} from '../../types/OfferDirection';

/**
 * Fixed presentation order. The offered vehicle is always step 1 and the
 * counterpart always step 2, whichever direction the visitor landed on, so the
 * numbering stays stable while switching.
 */
const SWITCHER_STEPS = [
  {direction: 'SELLING', labelKey: 'offerDirection.selling'},
  {direction: 'BUYING', labelKey: 'offerDirection.buying'},
] as const satisfies readonly {direction: OfferDirection; labelKey: string}[];

export function OfferDirectionSwitcher() {
  const {direction, counterpart} = useGetOnlineOfferData();
  const navigate = useNavigate();
  const location = useLocation();

  // A standalone offer has no counterpart, so there is nothing to switch to.
  if (isNil(counterpart)) {
    return null;
  }

  const goToCounterpart = () => {
    const pathname =
      counterpart.direction === 'BUYING'
        ? `/${counterpart.offerId}/buying`
        : `/${counterpart.offerId}`;

    navigate({pathname, search: location.search});
  };

  return (
    <StyledSwitcher data-testid="offerDirectionSwitcher">
      {SWITCHER_STEPS.map((step, index) => {
        const isActive = step.direction === direction;

        return (
          <StyledStep
            key={step.direction}
            type="button"
            disabled={isActive}
            aria-current={isActive ? 'page' : undefined}
            onClick={goToCounterpart}
            data-testid={`offerDirectionSwitcher-${step.direction.toLowerCase()}`}
          >
            <StyledBadge $isActive={isActive}>
              <Text size="small" alternative color={isActive ? 'inverted' : 'secondary'}>
                {index + 1}
              </Text>
            </StyledBadge>
            <Text size="small" alternative={isActive} color={isActive ? 'primary' : 'secondary'}>
              {i18n.t(step.labelKey)}
            </Text>
          </StyledStep>
        );
      })}
    </StyledSwitcher>
  );
}

const StyledSwitcher = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  padding: ${({theme}) => theme.getSize(1)};
  background-color: ${({theme}) => theme.colors.accent.lightest};
  border-radius: ${({theme}) => theme.radii.border_radius_full};
`;

const StyledStep = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => `${theme.getSize(2)} ${theme.getSize(4)}`};
  border: none;
  border-radius: ${({theme}) => theme.radii.border_radius_full};
  background-color: transparent;
  white-space: nowrap;

  &:not(:disabled) {
    cursor: pointer;
  }
`;

const StyledBadge = styled.span<{$isActive: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(6)};
  height: ${({theme}) => theme.getSize(6)};
  border-radius: ${({theme}) => theme.radii.border_radius_full};
  background-color: ${({theme, $isActive}) =>
    $isActive ? theme.colors.accent.base : theme.colors.fill.white};
`;
